import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { getSidebarMenuForUser } from "../config/sidebarNav";
import {
  APP_SIDEBAR_HOVER_EDGE_PX,
  APP_SIDEBAR_W,
} from "./layoutConstants";

/**
 * Narrow left rail — icon with name underneath.
 * Menu order is role-specific (see config/sidebarNav.js).
 */
const Sidebar = ({ sidebarOpen }) => {
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [edgeHover, setEdgeHover] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("currentUser") || "null");
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    setEdgeHover(false);
  }, [location.pathname]);

  const visible = Boolean(sidebarOpen || edgeHover);

  const menuItems = useMemo(
    () => getSidebarMenuForUser(currentUser),
    [currentUser],
  );

  const showComingSoon = (name) => {
    toast(`${name} — Coming Soon`, { id: `coming-soon-${name}` });
  };

  return (
    <>
      {!sidebarOpen && (
        <div
          className="fixed left-0 top-14 bottom-0 z-[45]"
          style={{ width: APP_SIDEBAR_HOVER_EDGE_PX }}
          onMouseEnter={() => setEdgeHover(true)}
          aria-hidden
        />
      )}

      <aside
        id="app-sidebar"
        className={`fixed left-0 top-14 bottom-0 z-[46] ${APP_SIDEBAR_W} flex flex-col bg-white border-r border-slate-200 transition-transform duration-200 ease-out ${
          visible
            ? "translate-x-0 shadow-md"
            : "-translate-x-full shadow-none pointer-events-none"
        }`}
        onMouseEnter={() => {
          if (!sidebarOpen) setEdgeHover(true);
        }}
        onMouseLeave={() => {
          if (!sidebarOpen) setEdgeHover(false);
        }}
        aria-label="Main navigation"
        aria-hidden={!visible}
      >
        <nav className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar py-2.5 px-1.5">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isComingSoon = Boolean(item.comingSoon);
              const isActive =
                !isComingSoon &&
                (location.pathname === item.path ||
                  (item.path !== "/" &&
                    location.pathname.startsWith(item.path)));

              const itemClass = `group/item flex flex-col items-center justify-center gap-1.5 rounded-xl px-1 py-2 min-h-[3.75rem] transition-colors ${
                isComingSoon
                  ? "text-slate-400 cursor-not-allowed"
                  : isActive
                    ? "text-indigo-700 bg-indigo-50"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`;

              const iconWrapClass = `inline-flex h-9 w-9 items-center justify-center rounded-lg ${
                isComingSoon
                  ? "bg-slate-50 text-slate-400"
                  : isActive
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-slate-100 text-slate-600 group-hover/item:bg-slate-200/80"
              }`;

              const content = (
                <>
                  <span className={iconWrapClass}>
                    <Icon size={18} />
                  </span>
                  <span className="w-full px-0.5 text-center text-[11px] font-bold leading-snug break-normal hyphens-none">
                    {item.name}
                  </span>
                </>
              );

              return (
                <li key={item.path}>
                  {isComingSoon ? (
                    <button
                      type="button"
                      title="Coming Soon"
                      aria-label={`${item.name} — Coming Soon`}
                      tabIndex={visible ? 0 : -1}
                      className={`w-full ${itemClass}`}
                      onClick={(e) => {
                        e.preventDefault();
                        showComingSoon(item.name);
                      }}
                    >
                      {content}
                    </button>
                  ) : (
                    <Link
                      to={item.path}
                      title={item.name}
                      tabIndex={visible ? 0 : -1}
                      className={itemClass}
                    >
                      {content}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
